import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Workflow } from 'lucide-react';
import { Callout, Card, PageHeader, Spinner } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { AgentChip } from '@/modules/agents/components/AgentChip';
import { skillLabel } from '@/modules/agents/components/labels';
import type { AgentIdentity } from '@/modules/agents/utils/agentLabels';
import { registryApi } from '../api/registryApi';
import { registryKeys } from '../queryKeys';
import { AgentFlowMap } from '../components/registry/AgentFlowMap';
import { SkillDetailDrawer } from '../components/registry/SkillDetailDrawer';
import { buildFlowGraph } from '../utils/registryView';
import styles from './AgencyFlowMapPage.module.css';

export function AgencyFlowMapPage() {
  const registry = useQuery({
    queryKey: registryKeys.registry(),
    queryFn: () => registryApi.getRegistry(),
  });
  const [params, setParams] = useSearchParams();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const roles = useMemo(() => registry.data?.roles ?? [], [registry.data]);
  const agents = useMemo<AgentIdentity[]>(
    () =>
      roles.map((r) => ({
        roleKey: r.key,
        personaName: r.persona?.name ?? r.displayName,
        displayName: r.displayName,
      })),
    [roles],
  );
  const requested = params.get('role');
  const roleKey = roles.some((r) => r.key === requested) ? requested! : (roles[0]?.key ?? null);
  const role = roles.find((r) => r.key === roleKey) ?? null;

  const graph = useMemo(() => {
    if (!roleKey) return null;
    const own = (registry.data?.skills ?? []).filter((s) => s.agentType === roleKey);
    return buildFlowGraph(own, skillLabel);
  }, [registry.data, roleKey]);

  const skillCount = graph ? graph.columns.filter((c) => c.tone !== 'trigger').reduce((n, c) => n + c.nodes.length, 0) : 0;
  const dormantCount = graph ? graph.columns.reduce((n, c) => n + c.nodes.filter((x) => x.dormant).length, 0) : 0;
  const stageCount = graph ? graph.columns.filter((c) => c.tone !== 'trigger').length : 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Agent Flow Map"
        subtitle="Each agent's pipeline, generated from role.json and the skill manifests: what triggers it, what it audits, what it proposes, and where it all lands — the one shared Tasks inbox."
      />

      {registry.isError && (
        <Callout variant="error" title="The registry could not load">
          agent-suite did not answer. Check that it is running and reachable from the backend.
        </Callout>
      )}

      <div className={styles.bar}>
        <div className={patterns.filterGroup} role="tablist" aria-label="Agent">
          {roles.map((r) => (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={r.key === roleKey}
              className={`${patterns.filterPill} ${r.key === roleKey ? patterns.filterPillActive : ''}`}
              onClick={() => setParams({ role: r.key })}
            >
              <span className={styles.tabLabel}>
                <AgentChip agentType={r.key} agents={agents} compact />
                {r.persona?.name ?? r.displayName} · {r.displayName}
              </span>
            </button>
          ))}
        </div>
        {graph && (
          <span className={patterns.pillMuted}>
            {skillCount} skill{skillCount === 1 ? '' : 's'} · {stageCount} stage{stageCount === 1 ? '' : 's'}
            {dormantCount > 0 ? ` · ${dormantCount} dormant` : ''}
          </span>
        )}
      </div>

      <Card padding="none">
        {registry.isLoading && <Spinner />}
        {graph && graph.columns.length > 0 && <AgentFlowMap graph={graph} onSelectSkill={setOpenKey} />}
        {graph && graph.columns.length === 0 && (
          <p className={styles.empty}>This agent has no skills on disk yet.</p>
        )}
      </Card>

      {role && role.guardrails.length > 0 && (
        <Card title={`${role.persona?.name ?? role.displayName}'s guardrails`} subtitle="From role.json — the lines no skill or engagement mode may cross.">
          <ul className={styles.guardrails}>
            {role.guardrails.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Card>
      )}

      <Callout icon={Workflow} variant="info" title="How to read it">
        Arrows follow the manifests: a trigger feeds the skills that declare it, an audit&apos;s
        findings route to the proposal skills that handle them, and every skill surfaces in the shared
        inbox. A dashed box is defined but not reachable; a red dot marks a skill that can hold a run
        for your approval.
      </Callout>

      <SkillDetailDrawer skillKey={openKey} agents={agents} onClose={() => setOpenKey(null)} />
    </div>
  );
}
